// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Data Export Service
// ═══════════════════════════════════════════════════════════════════════════
// Exports user data (favorites, journals, settings) as CSV or Markdown
// Supports download and share via Web Share API
// ═══════════════════════════════════════════════════════════════════════════

import * as storage from './storage.js';
import { getUserStats } from './analytics.js';

// ─────────────────────────────────────────────────────────────────────────
// CSV EXPORT
// ─────────────────────────────────────────────────────────────────────────

export async function exportToCSV() {
    const favorites = storage.getFavorites();
    const journals = storage.getJournalEntries();
    const settings = {
        theme: storage.getTheme(),
        soakTime: storage.getSoakTime(),
    };

    let csv = '';

    // Settings section
    csv += '# WRITTN DATA EXPORT\n';
    csv += `# Generated: ${new Date().toLocaleString()}\n\n`;

    // Favorites CSV
    csv += '## SAVED VERSES\n';
    csv += 'Reference,Text,Saved Date,Note\n';
    favorites.forEach(fav => {
        const text = escapeCSV(fav.text);
        const note = escapeCSV(fav.note || '');
        const date = fav.savedAt ? new Date(fav.savedAt).toLocaleDateString() : '';
        csv += `"${fav.reference}","${text}","${date}","${note}"\n`;
    });

    csv += '\n## JOURNAL ENTRIES\n';
    csv += 'Verse Reference,Entry Content,Created Date,Updated Date\n';
    journals.forEach(entry => {
        const content = escapeCSV(entry.content);
        const verseRef = escapeCSV(entry.verseRef);
        const created = entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : '';
        const updated = entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString() : '';
        csv += `"${verseRef}","${content}","${created}","${updated}"\n`;
    });

    csv += '\n## SETTINGS\n';
    csv += 'Setting,Value\n';
    csv += `"Theme","${settings.theme}"\n`;
    csv += `"Soak Time","${settings.soakTime} seconds"\n`;

    return csv;
}

function escapeCSV(str) {
    if (!str) return '';
    return str.replace(/"/g, '""').replace(/\n/g, ' ');
}

// ─────────────────────────────────────────────────────────────────────────
// MARKDOWN EXPORT
// ─────────────────────────────────────────────────────────────────────────

export async function exportToMarkdown() {
    const favorites = storage.getFavorites();
    const journals = storage.getJournalEntries();
    const settings = {
        theme: storage.getTheme(),
        soakTime: storage.getSoakTime(),
    };

    let stats;
    try {
        stats = await getUserStats();
    } catch {
        stats = null;
    }

    let md = '';

    // Header
    md += '# My Writtn Journey\n\n';
    md += `*Exported on ${new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })}*\n\n`;

    // Stats summary
    if (stats) {
        md += '## 📊 My Stats\n\n';
        md += `- **Verses Viewed:** ${stats.totalVersesViewed}\n`;
        md += `- **Verses Saved:** ${favorites.length}\n`;
        md += `- **Journal Entries:** ${journals.length}\n`;
        md += `- **Current Streak:** ${stats.currentStreak} days 🔥\n`;
        md += `- **Longest Streak:** ${stats.longestStreak} days\n`;
        md += `- **Days on Journey:** ${stats.daysSinceStart}\n`;
        md += '\n---\n\n';
    }

    // Saved Verses
    md += '## ❤️ Saved Verses\n\n';
    if (favorites.length === 0) {
        md += '*No saved verses yet*\n\n';
    } else {
        favorites.forEach(fav => {
            md += `### ${fav.reference}\n\n`;
            md += `> ${fav.text}\n\n`;
            if (fav.note) {
                md += `**My note:** ${fav.note}\n\n`;
            }
            if (fav.savedAt) {
                md += `*Saved on ${new Date(fav.savedAt).toLocaleDateString()}*\n\n`;
            }
            md += '---\n\n';
        });
    }

    // Journal Entries
    md += '## 📝 Journal Entries\n\n';
    if (journals.length === 0) {
        md += '*No journal entries yet*\n\n';
    } else {
        journals.forEach(entry => {
            md += `### ${entry.verseRef}\n\n`;
            if (entry.verseText) {
                md += `> ${entry.verseText}\n\n`;
            }
            md += `${entry.content}\n\n`;
            if (entry.createdAt) {
                md += `*Written on ${new Date(entry.createdAt).toLocaleDateString()}*\n\n`;
            }
            md += '---\n\n';
        });
    }

    // Settings
    md += '## ⚙️ My Settings\n\n';
    md += `- **Theme:** ${settings.theme}\n`;
    md += `- **Soak Time:** ${settings.soakTime} seconds\n\n`;

    // Footer
    md += '---\n\n';
    md += '*Exported from [Writtn](https://trywrittn.com) - Scripture written on your heart*\n';

    return md;
}

// ─────────────────────────────────────────────────────────────────────────
// DOWNLOAD & SHARE
// ─────────────────────────────────────────────────────────────────────────

export function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export async function shareFile(content, filename, mimeType) {
    // Check if Web Share API is available
    if (navigator.share && navigator.canShare) {
        try {
            const blob = new Blob([content], { type: mimeType });
            const file = new File([blob], filename, { type: mimeType });

            if (navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: 'My Writtn Data',
                    text: 'My saved verses and journal entries from Writtn',
                    files: [file]
                });
                return true;
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Share failed:', error);
            }
        }
    }

    // Fallback to download
    downloadFile(content, filename, mimeType);
    return false;
}

// ─────────────────────────────────────────────────────────────────────────
// CONVENIENCE FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────

export async function downloadCSV() {
    const csv = await exportToCSV();
    const date = new Date().toISOString().split('T')[0];
    downloadFile(csv, `writtn-export-${date}.csv`, 'text/csv');
}

export async function downloadMarkdown() {
    const md = await exportToMarkdown();
    const date = new Date().toISOString().split('T')[0];
    downloadFile(md, `my-writtn-journey-${date}.md`, 'text/markdown');
}

export async function shareCSV() {
    const csv = await exportToCSV();
    const date = new Date().toISOString().split('T')[0];
    return shareFile(csv, `writtn-export-${date}.csv`, 'text/csv');
}

export async function shareMarkdown() {
    const md = await exportToMarkdown();
    const date = new Date().toISOString().split('T')[0];
    return shareFile(md, `my-writtn-journey-${date}.md`, 'text/markdown');
}

export default {
    exportToCSV,
    exportToMarkdown,
    downloadFile,
    shareFile,
    downloadCSV,
    downloadMarkdown,
    shareCSV,
    shareMarkdown,
};
